import { useState } from '#imports'
import { computed } from 'vue'

import { throttledWatch, useWindowScroll } from '@vueuse/core'

const SCROLL_SHIFT = 10
export const SCROLL_THRESHOLD = 300

/**
 * @param previousScrollY The previous scroll position.
 * @param scrollY The current scroll position.
 * @param currentHiddenState The current hidden state of the header.
 */
export function shouldHideOnScroll(
  previousScrollY: number,
  scrollY: number,
  currentHiddenState: boolean,
): boolean {
  if (scrollY === 0) {
    return false
  }

  const goingDown = scrollY > previousScrollY
  const goingUp = scrollY < previousScrollY

  if (goingDown && scrollY > SCROLL_THRESHOLD) {
    return true
  }

  if (goingUp && scrollY < SCROLL_THRESHOLD) {
    return false
  }

  return currentHiddenState && previousScrollY - scrollY <= SCROLL_SHIFT
}

/**
 * A composable that hides the header when scrolling down and shows
 * it when scrolling up.
 *
 * @returns The hidden state is read-only, only
 * controlled by the scroll but can be forced to be hidden by setting the
 * forceHidden state
 */
export function useHideOnScroll() {
  const hidden = useState('header-hidden', () => false)
  // This is used to force the header to be hidden
  // if, under some external condition, we want to hide it
  const forceHidden = useState('header-force-hidden', () => false)

  const { y } = useWindowScroll()

  throttledWatch(
    y,
    (windowScrollY, previousScrollY = 0) => {
      if (windowScrollY === undefined) {
        return
      }
      hidden.value = shouldHideOnScroll(
        previousScrollY,
        windowScrollY,
        hidden.value,
      )
    },
    {
      throttle: 100,
      immediate: true,
    },
  )

  return {
    hidden: computed(() => forceHidden.value || hidden.value),
    forceHidden,
  }
}
